import { useState, useMemo, useEffect } from "react";
import { useApiCall } from "../custom-hooks/api-call";
import { useParams } from "react-router";

const C = {
    linen: "#F7ECDF", linenDeep: "#EFE1CF", ink: "#0A0C12", ink70: "#3A3D47",
    ink50: "#6B6E78", ink30: "#9A9CA4", card: "#FFFFFF", line: "#E7DAC6",
    lineSoft: "#F0E6D6", indigo: "#4F6EF7", indigoSoft: "#EAEEFF",
    green: "#1F9D6B", greenSoft: "#E4F5EC", amber: "#C9871A", amberSoft: "#FBEFD6",
    red: "#D0503C", redSoft: "#FBE6E0",
};

const thStyleDetail = { textAlign: "left", padding: "0 14px", height: 38, fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap", position: "sticky", top: 0, background: C.linenDeep };

function statusOf(p) { if (p >= 1) return "on_target"; if (p >= 0.9) return "on_track"; if (p >= 0.7) return "at_risk"; return "behind"; }
const STATUS_META = {
    on_target: { label: "On target", c: C.green, bg: C.greenSoft },
    on_track: { label: "On track", c: C.indigo, bg: C.indigoSoft },
    at_risk: { label: "At risk", c: C.amber, bg: C.amberSoft },
    behind: { label: "Behind", c: C.red, bg: C.redSoft },
    unassigned: { label: "Unassigned", c: C.gray, bg: C.graySoft }
};
function trendOf(h) { const avg = h.reduce((a, b) => a + b, 0) / h.length; const std = Math.sqrt(h.reduce((a, b) => a + (b - avg) ** 2, 0) / h.length); const s = h[h.length - 1] - h[0]; if (std < 0.12 && avg >= 0.95) return "consistent"; if (s > 0.18) return "improving"; if (s < -0.18) return "declining"; if (std >= 0.22) return "volatile"; return "steady"; }
const TREND_META = {
    consistent: { label: "Consistent", c: C.green },
    improving: { label: "Improving", c: C.indigo },
    steady: { label: "Steady", c: C.ink50 },
    volatile: { label: "Volatile", c: C.amber },
    declining: { label: "Declining", c: C.red }
};

const initials = (n) => { const p = n.split(" "); return (p[0][0] + (p[1] ? p[1][0] : "")).toUpperCase(); };

const FONT_DISP = "'Sora', system-ui, sans-serif";
const FONT_BODY = "'DM Sans', system-ui, sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, monospace";

function Pill({ label, color, bg }) {
    return (<span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 99, background: bg, color, fontSize: 11.5, fontWeight: 600 }}><span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />{label}</span>);
}
function SummaryCard({ label, value, sub, accent, bar }) {
    return (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
            <div style={{ fontFamily: FONT_DISP, fontSize: 26, fontWeight: 700, color: accent || C.ink, marginTop: 6, lineHeight: 1.1 }}>{value}</div>
            {bar != null && <div style={{ marginTop: 10 }}><ProgressBar value={bar} color={C.indigo} /></div>}
            {sub && <div style={{ fontSize: 12.5, color: C.ink50, marginTop: bar != null ? 8 : 6 }}>{sub}</div>}
        </div>
    );
}

const PERIODS = [
    { key: "today", label: "Today", factor: 1 / 22 }, { key: "yesterday", label: "Yesterday", factor: 1 / 22 },
    { key: "this_week", label: "This week", factor: 1 / 4.3 }, { key: "this_month", label: "This month", factor: 1 },
    { key: "last_30_days", label: "Last 30 days", factor: 1 }, { key: "last_month", label: "Last month", factor: 0.96 },
    { key: "rolling_quarter", label: "Rolling quarter", factor: 3 }, { key: "this_quarter", label: "This quarter", factor: 2.1 },
    { key: "last_quarter", label: "Last quarter", factor: 3 }, { key: "this_year", label: "This year", factor: 6.4 },
    { key: "all_time", label: "All time", factor: 27 },
    { key: "custom", label: "Custom" },
];

function money(n) { const a = Math.abs(n); const s = n < 0 ? "-" : ""; if (a >= 1e6) return s + "$" + (a / 1e6).toFixed(a >= 1e7 ? 1 : 2) + "M"; if (a >= 1e3) return s + "$" + (a / 1e3).toFixed(a >= 1e4 ? 0 : 1) + "K"; return s + "$" + Math.round(a); }
const pct = (x) => Math.round(x * 100) + "%";

function TrendChart({ categories, series, height = 240, yFormatter = money }) {
    const width = 680, padL = 58, padR = 18, padT = 18, padB = 30;
    const innerW = width - padL - padR, innerH = height - padT - padB;
    const allVals = series.flatMap(s => s.values);
    const maxV = Math.max(...allVals, 0.0001);
    const minV = Math.min(0, ...allVals);
    const span = (maxV - minV) || 1;
    const xFor = (i) => categories.length > 1 ? padL + (i / (categories.length - 1)) * innerW : padL + innerW / 2;
    const yFor = (v) => padT + innerH - ((v - minV) / span) * innerH;
    const ticks = 4;
    const tickVals = Array.from({ length: ticks + 1 }, (_, i) => minV + (span * i / ticks));
    return (
        <div>
            <div style={{ display: "flex", gap: 14, marginBottom: 10, flexWrap: "wrap" }}>
                {series.map(s => (
                    <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.ink70, fontWeight: 600 }}>
                        <span style={{ width: 16, height: 2, background: s.color, display: "inline-block", borderRadius: 2 }} />{s.label}
                    </span>
                ))}
            </div>
            <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
                {tickVals?.map((t, i) => (
                    <g key={i}>
                        <line x1={padL} x2={width - padR} y1={yFor(t)} y2={yFor(t)} stroke={C.lineSoft} strokeWidth="1" />
                        <text x={padL - 8} y={yFor(t) + 4} textAnchor="end" fontSize="10.5" fontFamily={FONT_MONO} fill={C.ink30}>{yFormatter(t)}</text>
                    </g>
                ))}
                {categories.map((c, i) => (
                    <text key={c + i} x={xFor(i)} y={height - 8} textAnchor="middle" fontSize="10.5" fontFamily={FONT_BODY} fill={C.ink50}>{c}</text>
                ))}
                {series.map(s => {
                    const d = s.values.map((v, i) => (i ? "L" : "M") + xFor(i).toFixed(1) + " " + yFor(v).toFixed(1)).join(" ");
                    return (
                        <g key={s.label}>
                            <path d={d} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            {s.values.map((v, i) => (
                                <circle key={i} cx={xFor(i)} cy={yFor(v)} r="3.2" fill={C.card} stroke={s.color} strokeWidth="2">
                                    <title>{`${categories[i]}: ${yFormatter(v)}`}</title>
                                </circle>
                            ))}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

function ProgressRing({ value, color, size = 132, stroke = 12 }) {
    const capped = Math.min(Math.max(value, 0), 1);
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - capped);
    return (
        <div style={{ position: "relative", width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.lineSoft} strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset .4s ease" }} />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div style={{ fontFamily: FONT_DISP, fontWeight: 700, fontSize: 22, color: C.ink }}>{pct(value)}</div>
                <div style={{ fontSize: 10, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>of target</div>
            </div>
        </div>
    );
}

function CompositionBar({ gross, net, refunds }) {
    const netPct = gross ? Math.max(0, net / gross) : 0, refPct = gross ? Math.max(0, refunds / gross) : 0;
    return (
        <div>
            <div style={{ display: "flex", height: 14, borderRadius: 99, overflow: "hidden", background: C.lineSoft }}>
                <div style={{ width: `${netPct * 100}%`, background: C.green }} />
                <div style={{ width: `${refPct * 100}%`, background: C.red }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11.5, color: C.ink50 }}>
                <span style={{ color: C.green, fontWeight: 600 }}>Net {pct(netPct)}</span>
                <span style={{ color: C.red, fontWeight: 600 }}>Refunded {pct(refPct)}</span>
            </div>
        </div>
    );
}

function Sparkline({ data, color, w = 60, h = 20 }) {
    const pad = 2; const min = Math.min(...data), max = Math.max(...data), span = max - min || 1;
    const pts = data.map((v, i) => [pad + (i / (data.length - 1)) * (w - pad * 2), h - pad - ((v - min) / span) * (h - pad * 2)]);
    const d = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
    return (<svg width={w} height={h} style={{ display: "block" }}><path d={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2" fill={color} /></svg>);
}
function ProgressBar({ value, color }) {
    const capped = Math.min(value, 1.25);
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative", flex: 1, height: 7, background: C.lineSoft, borderRadius: 99, overflow: "hidden" }}>
                <div style={{ position: "absolute", left: `${(1 / 1.25) * 100}%`, top: 0, bottom: 0, width: 1, background: C.ink30 }} />
                <div style={{ height: "100%", width: `${(capped / 1.25) * 100}%`, background: color, borderRadius: 99, transition: "width .35s ease" }} />
            </div>
            <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: C.ink, minWidth: 40, textAlign: "right" }}>{pct(value)}</span>
        </div>
    );
}

const selBar = { padding: "9px 12px", borderRadius: 10, border: `1px solid ${C.line}`, background: C.card, fontFamily: FONT_BODY, fontSize: 13.5, color: C.ink, outline: "none", cursor: "pointer" };

function Skeleton({ w = "100%", h = 14, r = 6, style = {} }) {
    return <div className="skeleton-shimmer" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

function AgentDetailSkeleton() {
    return (
        <div>
            <div style={{ display: "flex", gap: 16, alignItems: "center", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "18px 20px", marginBottom: 14, flexWrap: "wrap" }}>
                <Skeleton w={56} h={56} r={14} />
                <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 8 }}>
                    <Skeleton w={160} h={18} />
                    <Skeleton w={220} h={12} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <Skeleton w={92} h={24} r={99} />
                    <Skeleton w={92} h={24} r={99} />
                </div>
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: 10 }}>
                        <Skeleton w={80} h={11} />
                        <Skeleton w={110} h={22} />
                        <Skeleton w="100%" h={7} r={99} />
                    </div>
                ))}
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "14px 16px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 14 }}>
                {[5, 3, 8].map((n, row) => (
                    <div key={row} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <Skeleton w={78} h={11} />
                        {Array.from({ length: n }).map((_, i) => <Skeleton key={i} w={70} h={26} r={99} />)}
                    </div>
                ))}
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, alignItems: "stretch" }}>
                <div style={{ flex: "2 1 420px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", minWidth: 320 }}>
                    <Skeleton w={140} h={11} style={{ marginBottom: 14 }} />
                    <Skeleton w="100%" h={240} r={10} />
                </div>
                <div style={{ flex: "1 1 240px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, minWidth: 220 }}>
                    <Skeleton w={132} h={132} r={99} />
                    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
                        <Skeleton w={140} h={11} />
                        <Skeleton w="100%" h={14} r={99} />
                    </div>
                    <div style={{ width: "100%", paddingTop: 10, borderTop: `1px solid ${C.lineSoft}`, display: "flex", flexDirection: "column", gap: 8 }}>
                        <Skeleton w={110} h={11} />
                        <Skeleton w={110} h={26} />
                    </div>
                </div>
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
                <div style={{ padding: "0 14px", height: 38, display: "flex", alignItems: "center", gap: 24, borderBottom: `1px solid ${C.lineSoft}`, background: C.linenDeep }}>
                    {[60, 50, 50, 50, 50, 60, 170, 90].map((w, i) => <Skeleton key={i} w={w} h={10} style={{ marginLeft: i === 1 ? "auto" : 0 }} />)}
                </div>
                {Array.from({ length: 6 }).map((_, r) => (
                    <div key={r} style={{ padding: "12px 14px", display: "flex", alignItems: "center", gap: 24, borderTop: `1px solid ${C.lineSoft}` }}>
                        <Skeleton w={70} h={13} />
                        <Skeleton w={50} h={13} style={{ marginLeft: "auto" }} />
                        <Skeleton w={50} h={13} />
                        <Skeleton w={50} h={13} />
                        <Skeleton w={45} h={13} />
                        <Skeleton w={40} h={13} />
                        <Skeleton w={170} h={7} r={99} />
                        <Skeleton w={90} h={22} r={99} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function AgentDetailView({ rows, onBack }) {

    const [periodKey, setPeriodKey] = useState("this_month");
    const [pickerOpen, setPickerOpen] = useState(false);
    const period = PERIODS.find(p => p.key === periodKey);

    const {id} = useParams()

    const apiCall = useApiCall();

    const [metric, setMetric] = useState("net");
    const [compare, setCompare] = useState("none");
    const DEFAULT_KEYS = ["today", "this_week", "this_month", "this_quarter", "this_year", "all_time"];
    const [selKeys, setSelKeys] = useState(new Set(DEFAULT_KEYS));
    const [agentDetails, setAgentDetails] = useState({});
    const [agent, setAgent] = useState({});
    const [agentExtra, setAgentExtra] = useState({});

    const [agentLoad, setAgentLoad] = useState(false);
    const [detailLoad, setDetailLoad] = useState(false);

    

    //   if(!agent){
    //     return(
    //       <div style={{padding:60,textAlign:"center",color:C.ink50,background:C.card,border:`1px solid ${C.line}`,borderRadius:16}}>
    //         Agent not found.{" "}
    //         <button onClick={onBack} style={{color:C.indigo,background:"none",border:"none",cursor:"pointer",fontWeight:600}}>Go back</button>
    //       </div>
    //     );
    //   }

    //   const currentRow=rows.find(r=>r.id===agent.id)||{...computeRow(agent,period),status:"behind"};
    const currentRow = {};

    const sm = STATUS_META[agent?.status] || { label: "Unassigned", c: C.gray, bg: C.graySoft };
    const tm = TREND_META[agent?.trend] || { label: "--", c: C.gray };

    //   const teamRows=[...rows].filter(r=>r.team===agent.team).sort((a,b)=>b.progress-a.progress);
    const teamRows = []


    function togglePeriod(k) {
        setSelKeys(prev => { const n = new Set(prev); if (n.has(k)) { if (n.size > 1) n.delete(k); } else n.add(k); return n; });
    }
    const selectedPeriods = PERIODS.filter(p => selKeys.has(p.key));

    //   const report=useMemo(()=>selectedPeriods.map(p=>{
    //     // const r=computeRow(agent,p);r.status=statusOf(r.progress);return {...r,periodLabel:p.label,periodKey:p.key};
    //     const r = {}
    //   }),[agent,selKeys]); // eslint-disable-line

    const report = []

    //   const compareStats=useMemo(()=>{
    //     if(compare==="none")return null;
    //     return selectedPeriods.map(p=>{
    //     //   const compRows=AGENTS.map(a=>computeRow(a,p));
    //       const compRows = [];
    //       const pool=compare==="team"?compRows.filter(r=>r.team===agent.team):compRows;
    //       const n=pool.length||1;
    //       const sum=(k)=>pool.reduce((s,r)=>s+r[k],0);
    //       const targetSum=sum("target");
    //       return {target:targetSum/n,gross:sum("gross")/n,net:sum("net")/n,refunds:sum("refunds")/n,progress:targetSum?sum("net")/targetSum:0};
    //     });
    //   },[compare,selKeys,agent.team]); // eslint-disable-line

    const compareStats = []

    const metricLabel = { net: "Net", gross: "Gross", target: "Target", refunds: "Refunds", progress: "Progress" }[metric];
    const isPct = metric === "progress";
    const series = [{ label: agent?.name?.split(" ")[0], color: C.indigo, values: agentDetails?.periods?.map(r => r[metric]) }, (agentDetails?.compare) && {label: compare === "team" ? `${agent.team} avg` : "Company avg", color: C.ink30, values: agentDetails?.compare?.map(r => r[metric])}].filter(Boolean);
    // const series = [];
    // if (compareStats) series.push({ label: compare === "team" ? `${agent.team} avg` : "Company avg", color: C.ink30, values: compareStats.map(s => s[metric]) });

    const chip = (active) => ({ padding: "6px 12px", borderRadius: 99, border: `1px solid ${active ? C.indigo : C.line}`, background: active ? C.indigoSoft : C.card, color: active ? C.indigo : C.ink50, fontSize: 12.5, fontWeight: 600, cursor: "pointer" });
    const periodChip = (active) => ({ padding: "5px 10px", borderRadius: 99, border: `1px solid ${active ? C.ink : C.line}`, background: active ? C.ink : C.card, color: active ? C.card : C.ink50, fontSize: 11.5, fontWeight: 600, cursor: "pointer" });

    // ---- custom date range state ----
    const [showCustomPicker, setShowCustomPicker] = useState(false);
    const [draftStart, setDraftStart] = useState("");
    const [draftEnd, setDraftEnd] = useState("");
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");

    function formatDateLabel(d) { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    const customRangeLabel = customStart && customEnd ? `${formatDateLabel(customStart)} – ${formatDateLabel(customEnd)}` : "Custom";
    const periodLabel = periodKey === "custom" ? customRangeLabel : period.label;

    function openPicker() {
        setPickerOpen(o => {
            const next = !o;
            if (next) { setShowCustomPicker(periodKey === "custom"); setDraftStart(customStart); setDraftEnd(customEnd); }
            return next;
        });
    }
    function selectPeriod(key) { setPeriodKey(key); setPickerOpen(false); setShowCustomPicker(false); }
    function openCustomPicker() { setShowCustomPicker(true); setDraftStart(customStart); setDraftEnd(customEnd); }
    function applyCustomRange() {
        if (!draftStart || !draftEnd || draftStart > draftEnd) return;
        setCustomStart(draftStart); setCustomEnd(draftEnd);
        setPeriodKey("custom"); setShowCustomPicker(false); setPickerOpen(false);
    }
    function cancelCustomRange() { setShowCustomPicker(false); }

    const getAgentDetails = async () => {
        setDetailLoad(false);
        try {
            const apiRes = await apiCall({ method: "get", url: `agents/${id}/periods?compare=${compare}&periods=${[...selKeys].join(',')}` })
            setAgentDetails(apiRes.data)
            setDetailLoad(true)
        } catch (error) {
            console.log("Error", error)
        }
    }

    const getAgent = async () => {
        if(periodKey==="custom"&&(!customStart||!customEnd))return;
        setAgentLoad(false);
        try {
            const rangeQs=periodKey==="custom"?`&from=${customStart}&to=${customEnd}`:"";
            const apiRes = await apiCall({ method: "get", url: `agents/${id}?period=${periodKey}${rangeQs}` })
            setAgent(apiRes?.data?.agent);
            setAgentLoad(true);
            setAgentExtra({ rank: apiRes?.data?.rank, teamCount: apiRes?.data?.teamCount, teamAvgProgress: apiRes?.data?.teamAvgProgress })
        } catch (error) {
            console.log("Error", error)
        }
    }

    useEffect(() => {
        getAgent()
    }, [periodKey,customStart,customEnd])

    useEffect(() => {
        getAgentDetails()
    }, [compare, selKeys])

    return (
        <div style={{ minHeight: "100vh", background: C.linen, fontFamily: FONT_BODY, color: C.ink, padding: "24px 28px 40px" }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
                *{box-sizing:border-box}table{border-collapse:collapse}
                ::-webkit-scrollbar{height:9px;width:9px}::-webkit-scrollbar-thumb{background:#D8C7AF;border-radius:9px}
                input::placeholder{color:${C.ink30}}
                .skeleton-shimmer{position:relative;overflow:hidden;background:${C.lineSoft};display:inline-block}
                .skeleton-shimmer::after{content:"";position:absolute;inset:0;transform:translateX(-100%);background:linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent);animation:skeleton-shimmer 1.4s ease-in-out infinite}
                @keyframes skeleton-shimmer{100%{transform:translateX(100%)}}
            `}</style>

            <div style={{ maxWidth: 1240, margin: "0 auto" }}>
                {/* header */}
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 18 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.indigo, textTransform: "uppercase", letterSpacing: 1.4 }}>Legendesk · Sales</div>
                        <h1 style={{ fontFamily: FONT_DISP, fontSize: 30, fontWeight: 700, margin: "4px 0 0", letterSpacing: -0.5 }}>Agent Performance</h1>
                    </div>
                    <div style={{ position: "relative" }}>
                        <button onClick={openPicker} style={{ ...selBar, display: "inline-flex", alignItems: "center", gap: 10, fontWeight: 600 }}>
                            <span style={{ width: 7, height: 7, borderRadius: 99, background: C.indigo }} />{periodLabel}<span style={{ color: C.ink30, fontSize: 11 }}>▼</span>
                        </button>
                        {pickerOpen && (
                            <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: 6, boxShadow: "0 12px 30px rgba(10,12,18,.12)", zIndex: 20, width: 220 }}>
                                {PERIODS.filter(p => p.key !== "custom").map(p => (<button key={p.key} onClick={() => selectPeriod(p.key)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: p.key === periodKey ? 600 : 400, color: p.key === periodKey ? C.indigo : C.ink70, background: p.key === periodKey ? C.indigoSoft : "transparent" }}>{p.label}</button>))}
                                <button onClick={openCustomPicker} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "none", cursor: "pointer", fontFamily: FONT_BODY, fontSize: 13.5, fontWeight: periodKey === "custom" ? 600 : 400, color: periodKey === "custom" ? C.indigo : C.ink70, background: periodKey === "custom" ? C.indigoSoft : "transparent" }}>
                                    {periodKey === "custom" ? customRangeLabel : "Custom…"}
                                </button>
                                {showCustomPicker && (
                                    <div style={{ padding: 10, borderTop: `1px solid ${C.lineSoft}`, marginTop: 4, display: "flex", flexDirection: "column", gap: 8 }}>
                                        <label style={{ fontSize: 11.5, color: C.ink50, fontWeight: 600 }}>Start date
                                            <input type="date" value={draftStart} max={draftEnd || undefined} onChange={e => setDraftStart(e.target.value)} style={{ display: "block", width: "100%", marginTop: 4, padding: "6px 8px", borderRadius: 8, border: `1px solid ${C.line}`, fontFamily: FONT_BODY, fontSize: 13 }} />
                                        </label>
                                        <label style={{ fontSize: 11.5, color: C.ink50, fontWeight: 600 }}>End date
                                            <input type="date" value={draftEnd} min={draftStart || undefined} onChange={e => setDraftEnd(e.target.value)} style={{ display: "block", width: "100%", marginTop: 4, padding: "6px 8px", borderRadius: 8, border: `1px solid ${C.line}`, fontFamily: FONT_BODY, fontSize: 13 }} />
                                        </label>
                                        <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                                            <button onClick={applyCustomRange} disabled={!draftStart || !draftEnd || draftStart > draftEnd} style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: "none", cursor: (!draftStart || !draftEnd || draftStart > draftEnd) ? "not-allowed" : "pointer", background: C.indigo, color: "#fff", fontSize: 12.5, fontWeight: 600, opacity: (!draftStart || !draftEnd || draftStart > draftEnd) ? 0.5 : 1 }}>Apply</button>
                                            <button onClick={cancelCustomRange} style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: `1px solid ${C.line}`, cursor: "pointer", background: C.card, color: C.ink70, fontSize: 12.5, fontWeight: 600 }}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {agentLoad && detailLoad ?
                    <div>
                        {/* identity header */}
                        <div style={{ display: "flex", gap: 16, alignItems: "center", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "18px 20px", marginBottom: 14, flexWrap: "wrap" }}>
                            <div style={{ width: 56, height: 56, borderRadius: 14, background: sm?.bg, color: sm.c, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20, flexShrink: 0 }}>{initials("agent name")}</div>
                            <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ fontFamily: FONT_DISP, fontWeight: 700, fontSize: 20 }}>{agent?.name}</div>
                                <div style={{ fontSize: 12.5, color: C.ink50, marginTop: 2 }}> {agent?.team} · Rank #{agentExtra?.rank} of {agentExtra?.teamCount}</div>
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <Pill label={sm.label} color={sm.c} bg={sm.bg} />
                                <Pill label={tm.label} color={tm.c} bg={C.linenDeep} />
                            </div>
                        </div>

                        {/* summary cards */}
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
                            <SummaryCard label="Target" value={money(agent?.target)} sub={period?.label} />
                            <SummaryCard label="Net achieved" value={money(agent?.net)} bar={agent?.progress} />
                            <SummaryCard label="Gross achieved" value={money(agent?.gross)} sub={`Net after refunds: ${money(agent?.net)}`} />
                            <SummaryCard label="Lost revenue" value={money(agent?.refunds)} accent={C.red} sub={`Refund ratio ${pct(agent?.refundRatio)}`} />
                            <SummaryCard label="Pod average" value={pct(agentExtra?.teamAvgProgress)} sub={`Agent is ${agent?.progress >= agentExtra?.teamAvgProgress ? "above" : "below"} Pod ${agent?.team} average`} />
                        </div>

                        {/* filters */}
                        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "14px 16px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 12 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 78 }}>Metric</span>
                                {[["net", "Net"], ["gross", "Gross"], ["target", "Target"], ["refunds", "Refunds"], ["progress", "Progress"]].map(([k, lbl]) => (
                                    <button key={k} onClick={() => setMetric(k)} style={chip(metric === k)}>{lbl}</button>
                                ))}
                            </div>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 78 }}>Compare</span>
                                {[["none", "No comparison"], ["team", `${agent.team} avg`], ["company", "Company avg"]].map(([k, lbl]) => (
                                    <button key={k} onClick={() => setCompare(k)} style={chip(compare === k)}>{lbl}</button>
                                ))}
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: C.ink50, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 78 }}>Periods</span>
                                {PERIODS?.filter((p) => p.label != "Custom").map(p => (
                                    <button key={p.key} onClick={() => togglePeriod(p.key)} style={periodChip(selKeys.has(p.key))}>{p.label}</button>
                                ))}
                            </div>
                        </div>

                        {/* charts row */}
                        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, alignItems: "stretch" }}>
                            <div style={{ flex: "2 1 420px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", minWidth: 320 }}>
                                <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{metricLabel} across periods</div>
                                <TrendChart categories={agentDetails?.periods?.map(p => p.periodLabel)} series={series} yFormatter={isPct ? pct : money} />
                            </div>
                            <div style={{ flex: "1 1 240px", background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, padding: "16px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, minWidth: 220 }}>
                                <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, alignSelf: "flex-start" }}>Progress this period</div>
                                <ProgressRing value={agent?.progress} color={sm.c} />
                                <div style={{ width: "100%" }}>
                                    <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Revenue composition</div>
                                    <CompositionBar gross={agent?.gross} net={agent?.net} refunds={agent?.refunds} />
                                </div>
                                <div style={{ width: "100%", paddingTop: 10, borderTop: `1px solid ${C.lineSoft}` }}>
                                    <div style={{ fontSize: 12, color: C.ink50, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>6-period pattern</div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <Sparkline data={agent.history} color={tm.c} w={90} h={26} />
                                        <span style={{ fontSize: 12.5, fontWeight: 600, color: tm.c }}>{tm.label}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* report table */}
                        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 16, overflow: "hidden" }}>
                            <div style={{ overflow: "auto" }}>
                                <table style={{ width: "100%", minWidth: 760 }}>
                                    <thead><tr>
                                        <th style={thStyleDetail}>Period</th>
                                        <th style={{ ...thStyleDetail, textAlign: "right" }}>Target</th>
                                        <th style={{ ...thStyleDetail, textAlign: "right" }}>Gross</th>
                                        <th style={{ ...thStyleDetail, textAlign: "right" }}>Net</th>
                                        <th style={{ ...thStyleDetail, textAlign: "right" }}>Lost</th>
                                        <th style={{ ...thStyleDetail, textAlign: "right" }}>Refund %</th>
                                        <th style={{ ...thStyleDetail, width: 170 }}>Progress</th>
                                        <th style={thStyleDetail}>Status</th>
                                    </tr></thead>
                                    <tbody>
                                        {agentDetails?.periods?.map((r, i) => {

                                            const rsm = STATUS_META[r.status];
                                            const bad = r.refundRatio >= 0.15;
                                            return (
                                                <tr key={r.periodKey} style={{ borderTop: `1px solid ${C.lineSoft}`, background: i % 2 ? "#FffdF9" : C.card }}>
                                                    <td style={{ padding: "10px 14px", fontWeight: 600, fontSize: 13 }}>{r.periodLabel}</td>
                                                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.ink70 }}>{money(r.target)}</td>
                                                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13 }}>{money(r.gross)}</td>
                                                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, fontWeight: 500 }}>{money(r.net)}</td>
                                                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: C.red }}>{money(r.refunds)}</td>
                                                    <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 13, color: bad ? C.red : C.ink70, fontWeight: bad ? 600 : 400 }}>{pct(r.refundRatio)}</td>
                                                    <td style={{ padding: "10px 14px", width: 170 }}><ProgressBar value={r.progress} color={rsm?.c} /></td>
                                                    <td style={{ padding: "10px 14px" }}><Pill label={rsm?.label} color={rsm?.c} bg={rsm?.bg} /></td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.lineSoft}`, fontSize: 12, color: C.ink50 }}>
                                Showing {report.length} of {PERIODS.length} periods · toggle periods above to add or remove rows
                            </div>
                        </div>
                    </div>
                    :
                    <AgentDetailSkeleton />
                }
            </div>
        </div>
    );
}

export default AgentDetailView